import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { Button, Chip, Collapse, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { useCallback, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import TextAreaInput from '@/modules/base/components/inputs/textarea-input/textarea-input'
import { FeedbackData, FeedbackRating, messageFeedbackUrl } from '@/redux/ducks/ai/chat'
import { toast } from '@/modules/base/components/toast/toastConfig'
import request from '@/modules/base/util/request'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  feedbackPanel: {
    position: 'relative',
    marginTop: 12,
    padding: 16,
    backgroundColor: theme.white,
    borderRadius: 4,
    border: `1px solid ${theme.borderColor}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  feedbackLabel: {
    marginBottom: 8,
    fontWeight: 500,
    color: theme.text,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: theme.backgroundColor,
    '&:hover': {
      backgroundColor: theme.primaryLight,
      color: theme.white,
    },
  },
  tagSelected: {
    backgroundColor: theme.primary,
    color: theme.white,
    '&:hover': {
      backgroundColor: theme.primaryDark,
      color: theme.white,
    },
  },
  commentField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
    },
  },
  feedbackActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  closeIcon: {
    position: 'absolute',
    top: 12,
    right: 16,
    cursor: 'pointer',
    '&:hover': {
      color: theme.primaryV2,
    },
  },
}))
interface AiFeedbackPanelProps {
  messageId: string
  feedbackExpanded: boolean
  pendingFeedbackType: FeedbackRating,
  onFeedbackSubmitted: (feedbackData: FeedbackData) => void
  onFeedbackCancelled: () => void
}

export default function AiFeedbackPanel(props: AiFeedbackPanelProps) {
  const classes = useStyles()
  const {
    messageId,
    feedbackExpanded,
    pendingFeedbackType,
    onFeedbackSubmitted,
    onFeedbackCancelled,
  } = props

  const contentEndRef = useRef<HTMLDivElement>(null)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [comment, setComment] = useState('')

  const onCollapseEntered = useCallback(() => {
    contentEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [])

  const onTagToggle = useCallback((tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag],
    )
  }, [])

  const onCancelFeedback = useCallback(() => {
    setSelectedTags([])
    setComment('')
    onFeedbackCancelled?.()
  }, [])

  const onSubmitFeedback = useCallback(async() => {
    try {
      const feedbackData: FeedbackData = {
        rating: pendingFeedbackType,
        tags: selectedTags,
        comment,
      }

      await request(messageFeedbackUrl(messageId), {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: feedbackData,
      })

      onFeedbackSubmitted?.(feedbackData)
    } catch {
      toast.showToast({ type: 'error', message: lang.ai.chat.feedback.errors.default })
    }

  }, [pendingFeedbackType, selectedTags, comment])

  const feedbackTags = pendingFeedbackType === 'up' ? lang.ai.chat.feedback.tags.positive : lang.ai.chat.feedback.tags.negative
  return (
    <>
      <Collapse in={feedbackExpanded} onEntered={onCollapseEntered}>
        <div className={classes.feedbackPanel}>
          <CloseRoundedIcon className={classes.closeIcon} onClick={onCancelFeedback} fontSize="small" />
          <div>
            <Typography variant="body2" className={classes.feedbackLabel}>
              {lang.ai.chat.feedback.tags.title}
            </Typography>
            <div className={classes.tagsContainer}>
              {feedbackTags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onClick={() => onTagToggle(tag)}
                  className={`${classes.tag} ${selectedTags.includes(tag) ? classes.tagSelected : ''}`}
                />
              ))}
            </div>
          </div>

          <div>
            <Typography variant="body2" className={classes.feedbackLabel}>
              {lang.ai.chat.feedback.comment.title}
            </Typography>
            <TextAreaInput
              value={comment}
              name="comment"
              dataQsId="ai-feedback-comment"
              onChange={(value) => {
                setComment(value)
              }}
              placeholder={lang.ai.chat.feedback.comment.placeholder}
              className={classes.commentField}
            />
          </div>

          <div className={classes.feedbackActions}>
            <Button
              variant="outlined"
              size="small"
              onClick={onCancelFeedback}
              color="customGrey"
            >
              {lang.generics.abort}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={onSubmitFeedback}
              disabled={!pendingFeedbackType}
            >
              {lang.sendButtonLabel}
            </Button>
          </div>
        </div>
        <div ref={contentEndRef} />
      </Collapse>
    </>
  )
}
